import type { CheckoutDetailResponse, CheckoutVehicleInfo } from './dto.inventory';

/*
|--------------------------------------------------------------------------
| TRANSFORMED CHECKOUT DETAIL TYPES
|--------------------------------------------------------------------------
*/

export type TransformedVehicleOption = {
  label: string;
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

export type TransformedFeature = {
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

export type PricingSummaryItem = {
  label: string;
  value: number;
};

export type TransformedCheckoutDetail = {
  // Vehicle identification
  vin: string;
  shortcode: string;
  vehicleImage: string;

  // Vehicle details
  model: string;
  modelYear: string;
  trim: string;
  type: string;
  availability: string;

  // Currency
  currencyCode: string;

  // Vehicle options from vehicleInfo (excludes 'features' label)
  vehicleOptions: TransformedVehicleOption[];

  // Features from vehicleInfo 'features' content
  features: TransformedFeature[];

  // Pricing breakdown
  pricing: {
    basePrice: number;
    addOns: number;
    subtotal: number;
    fees: PricingSummaryItem[];
    credits: PricingSummaryItem[];
    deposit: number;
    total: number;
  };

  // Right side summary (optional - used in quote view)
  rightSideSummary?: {
    prospectType: string;
    preferredPaymentMethod: string;
    deliveryTimeline: string;
    companyName: string;
    companyTaxIdentification: string;
    invoiceAddressLine1: string;
    invoiceCity: string;
    invoicePostalCode: string;
  };

  // Docusign status (optional - used in quote view)
  docusignStatus?: string;

  // Current quote status (optional - used to disable form when 'Expired' or 'Shared')
  currentStatus?: string;
};

/*
|--------------------------------------------------------------------------
| HELPER FUNCTIONS
|--------------------------------------------------------------------------
*/

const RENDERCHAIN_BASE_URL = 'https://renderchain.lucidmotors.com';

/**
 * Maps variant SAP codes to renderchain URL path slugs
 * Renderchain expects 'grand_touring', 'touring', 'pure' (with underscores)
 */
const VARIANT_TO_PATH_MAP: Record<string, string> = {
  GT: 'grand_touring',
  AT: 'touring',
  PURE: 'pure',
};

type RenderchainImageConfig = {
  model: string;
  modelYear: string;
  variant: string;
  edition: string;
  exteriorColor: string;
  roof: string;
  appearance: string;
  wheels: string;
  interior: string;
  driverAssistance: string;
  width?: number;
  height?: number;
  camera?: string;
};

/**
 * Builds the renderchain vehicle image URL from vehicle configuration
 * Format: https://renderchain.lucidmotors.com/640x480/filters:format(.webp)/filters:sharpen(1,0,false)/filters:quality(90)/renderchain/{model}/{year}/{variant}/CAM_EXT_08/studio/3840-2880/{filename}.png
 * Filename format: {MODEL}-{VARIANT}-{EDITION}-{COLOR}-SCL0-{ROOF}-{APPEARANCE}-{WHEELS}-{INTERIOR}-{ADAS}-RENA.png
 */
const buildRenderchainImageUrl = (config: RenderchainImageConfig): string => {
  const {
    model,
    modelYear,
    variant,
    edition,
    exteriorColor,
    roof,
    appearance,
    wheels,
    interior,
    driverAssistance,
    width = 640,
    height = 480,
    camera = 'CAM_EXT_08',
  } = config;

  // Normalize values
  const modelUpper = model.toUpperCase();
  const modelLower = model.toLowerCase();
  const variantUpper = variant.toUpperCase();
  const editionCode = edition || 'STD';

  // Map variant code to path slug (GT -> grand-touring, AT -> touring, PURE -> pure)
  const variantPath = VARIANT_TO_PATH_MAP[variantUpper] || variantUpper.toLowerCase();

  // Build filename: AIR-PURE-STD-L901-SCL0-RF00-EXT01-WH24-INT18-AD01-RENA.png
  const filename = [
    modelUpper,
    variantUpper,
    editionCode,
    exteriorColor,
    'SCL0', // Standard value
    roof,
    appearance,
    wheels,
    interior,
    driverAssistance,
    'RENA', // Standard suffix
  ].join('-');

  // Build full URL with transparent background
  const baseUrl = [
    RENDERCHAIN_BASE_URL,
    `${width}x${height}`,
    'filters:format(.webp)',
    'filters:sharpen(1,0,false)',
    'filters:quality(90)',
    'renderchain',
    modelLower,
    modelYear,
    variantPath,
    camera,
    'alpha',
    '3840-2880',
    `${filename}.png`,
  ].join('/');

  // Add transparent background parameter
  return `${baseUrl}`;
};

/**
 * Builds the vehicle image URL from checkout response data
 * Uses vehicleDetailsData to extract configuration codes (sapCode)
 *
 * IMPORTANT: Use `look.sapCode` (e.g., "EXT07") for the appearance/look code in the image URL.
 * Do NOT use `appearance.sapCode` (e.g., "EXT07M") - it includes a roof suffix that renderchain doesn't recognize.
 */
const buildVehicleImageFromResponse = (response: CheckoutDetailResponse): string => {
  const { vehicleDetailsData, checkoutData } = response;

  // Use sapCode for proper codes
  // Defaults based on working renderchain URL: AIR-GT-STD-L205-SCL0-RF00-EXT07-WH22-INT06-AD01-RENA
  const model = vehicleDetailsData?.model?.sapCode || 'AIR';
  const modelYear = vehicleDetailsData?.modelYear || checkoutData?.modelYear || '2025';
  const variant = vehicleDetailsData?.modelVariant?.sapCode || 'GT';
  const edition = vehicleDetailsData?.edition?.sapCode || 'STD';
  const exteriorColor = vehicleDetailsData?.color?.sapCode || 'L205';
  const roof = vehicleDetailsData?.roof?.sapCode || 'RF00';
  // Use 'look' (e.g., EXT07) NOT 'appearance' (e.g., EXT07M) - appearance includes roof suffix
  const look = vehicleDetailsData?.look?.sapCode || 'EXT07';
  const wheels = vehicleDetailsData?.wheels?.sapCode || 'WH22';
  const interior = vehicleDetailsData?.interior?.sapCode || 'INT06';
  const driverAssistance = vehicleDetailsData?.driverAssistance?.sapCode || 'AD01';

  return buildRenderchainImageUrl({
    model,
    modelYear,
    variant,
    edition,
    exteriorColor,
    roof,
    appearance: look, // Pass look value as appearance for the filename
    wheels,
    interior,
    driverAssistance,
    width: 560,
    height: 268,
  });
};

/**
 * Transforms vehicleInfo items to vehicle options (excludes 'features' and 'model' labels)
 * @param vehicleInfo - Array of vehicle info items from checkout data
 * @returns Array of transformed vehicle options
 */
const transformVehicleOptions = (
  vehicleInfo: CheckoutVehicleInfo[] | undefined
): TransformedVehicleOption[] => {
  if (!vehicleInfo) return [];

  return vehicleInfo
    .filter((item) => item.label !== 'features' && item.label !== 'model' && item.label)
    .map((item) => ({
      label: item.label || '',
      name: item.name || '',
      code: item.code || '',
      type: item.type || '',
      value: item.value ?? 0,
      description: item.description,
    }));
};

/**
 * Extracts features from the vehicleInfo 'features' item
 * @param vehicleInfo - Array of vehicle info items from checkout data
 * @returns Array of transformed features
 */
const extractFeatures = (vehicleInfo: CheckoutVehicleInfo[] | undefined): TransformedFeature[] => {
  if (!vehicleInfo) return [];

  const featuresItem = vehicleInfo.find((item) => item.label === 'features');
  if (!featuresItem?.contents) return [];

  return featuresItem.contents.map((feature) => ({
    name: feature.name || '',
    code: feature.code || '',
    type: feature.type || '',
    value: feature.value ?? 0,
    description: feature.description,
  }));
};

/**
 * Extracts the base price (variant value) from vehicle info array
 * @param vehicleInfo - Array of vehicle info items from checkout data
 * @returns The base price value or 0 if not found
 */
const extractBasePrice = (vehicleInfo: CheckoutVehicleInfo[] | undefined): number => {
  const variantItem = vehicleInfo?.find((item) => item.label === 'variant');
  return variantItem?.value ?? 0;
};

/*
|--------------------------------------------------------------------------
| TRANSFORMER FUNCTION
|--------------------------------------------------------------------------
*/

/**
 * Transforms the checkout detail API response to a component-friendly format
 * Uses primarily checkoutData which contains all needed information
 * @param response - The raw checkout detail API response
 * @returns Transformed checkout detail object
 * @example
 * ```typescript
 * const transformed = transformCheckoutDetail(apiResponse);
 * console.log(transformed.model); // 'air'
 * console.log(transformed.vehicleOptions[0].name); // 'Touring'
 * ```
 */
export const transformCheckoutDetail = (
  response: CheckoutDetailResponse
): TransformedCheckoutDetail => {
  const { checkoutData } = response;

  const shortcode = checkoutData?.shortcode || '';
  const pricing = checkoutData?.pricing;

  // Build vehicle image URL from response data
  const vehicleImage = buildVehicleImageFromResponse(response);

  // Extract pricing values
  const basePrice = extractBasePrice(checkoutData?.vehicleInfo);
  const addOns = pricing?.addOns?.value ?? 0;
  const subtotal = pricing?.subtotal?.value ?? 0;
  const deposit = pricing?.deposit?.value ?? 0;
  const total = pricing?.total?.value ?? 0;

  // Extract fees and credits
  const fees: PricingSummaryItem[] =
    pricing?.fees?.map((fee) => ({
      label: fee.name,
      value: fee.value,
    })) ?? [];

  const credits: PricingSummaryItem[] =
    pricing?.credits?.map((credit) => ({
      label: credit.name,
      value: credit.value,
    })) ?? [];

  return {
    // Vehicle identification
    vin: checkoutData?.vin || '',
    shortcode,
    vehicleImage,

    // Vehicle details
    model: checkoutData?.model || '',
    modelYear: checkoutData?.modelYear || '',
    trim: checkoutData?.trim || '',
    type: checkoutData?.type || '',
    availability: checkoutData?.availability || '',

    // Currency
    currencyCode: pricing?.currencyCode || 'USD',

    // Vehicle options and features
    vehicleOptions: transformVehicleOptions(checkoutData?.vehicleInfo),
    features: extractFeatures(checkoutData?.vehicleInfo),

    // Pricing breakdown
    pricing: {
      basePrice,
      addOns,
      subtotal,
      fees,
      credits,
      deposit,
      total,
    },
  };
};
